/*
 * <twc-reviews-feed> — the Reviews.io Polaris "elements" feed, one script per
 * document however many feeds a page holds.
 *
 * What the widget is:
 *   loader   https://widget.reviews.io/polaris/build.js  (defer)
 *   call     new window.ReviewsWidget('#target', {store, widget:'polaris',
 *            options, translations, styles})
 *   renders  INLINE light DOM — a Vue app under `.ElementsWidget-prefix`, with
 *            its stylesheets injected into <head>. No iframe, so the section
 *            can skin it with ordinary CSS.
 *   styles   the `styles` map is compiled by the widget into ONE data: URL
 *            stylesheet holding `.ElementsWidget-prefix .ElementsWidget { … }`
 *            — two classes, specificity 0-2-0. Anything the section needs to
 *            correct afterwards outranks it on specificity alone, which is why
 *            this build carries no CSS importance flags.
 *
 * Loading rules, in order of importance:
 *   1. ONE loader tag per document. The promise is module-level and the tag
 *      carries `data-twc-reviewsio`, so a second instance, a re-render or a
 *      duplicated asset include all reuse the one fetch.
 *   2. The loader is injected on demand, never at parse time, and only once the
 *      feed is near the viewport (the band always sits below the fold). In the
 *      theme editor it loads immediately so a merchant sees real content.
 *   3. A blocked or dead CDN must cost the page nothing and say nothing: the
 *      element flips to `data-state="unavailable"`, the section collapses, and
 *      no console output is produced.
 *   4. Every listener and observer rides one AbortController.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-reviews-feed')) return;

  var LOADER_SRC = 'https://widget.reviews.io/polaris/build.js';
  var READY_TIMEOUT_MS = 15000;
  var READY_POLL_MS = 100;
  var NEAR_VIEWPORT = '600px';

  var loaderPromise = null;

  /* ---- 1. the single loader --------------------------------------------- */

  function widgetReady() {
    return typeof global.ReviewsWidget === 'function';
  }

  /*
   * build.js defines window.ReviewsWidget some time after its own load event
   * (it boots a Vue runtime first), so "script loaded" is not "widget ready".
   * Poll for the constructor and give up rather than hang forever.
   */
  function awaitConstructor() {
    return new Promise(function (resolve, reject) {
      if (widgetReady()) {
        resolve();
        return;
      }
      var waited = 0;
      var timer = global.setInterval(function () {
        if (widgetReady()) {
          global.clearInterval(timer);
          resolve();
          return;
        }
        waited += READY_POLL_MS;
        if (waited >= READY_TIMEOUT_MS) {
          global.clearInterval(timer);
          reject(new Error('twc-reviews-feed: widget did not initialise'));
        }
      }, READY_POLL_MS);
    });
  }

  function loadWidgetScript() {
    if (widgetReady()) return Promise.resolve();
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-twc-reviewsio]');

      if (existing) {
        // A tag is already in flight (the app embed, or a duplicated include).
        // Wait on the constructor rather than asking the CDN for a second copy.
        existing.addEventListener('error', function () {
          reject(new Error('twc-reviews-feed: loader failed'));
        });
        awaitConstructor().then(resolve, reject);
        return;
      }

      var script = document.createElement('script');
      script.src = LOADER_SRC;
      script.defer = true;
      script.setAttribute('data-twc-reviewsio', '');
      script.addEventListener('load', function () {
        awaitConstructor().then(resolve, reject);
      });
      script.addEventListener('error', function () {
        reject(new Error('twc-reviews-feed: loader failed'));
      });
      document.head.appendChild(script);
    });

    return loaderPromise;
  }

  /* ---- 2. the element ---------------------------------------------------- */

  var TwcReviewsFeed = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();

      this.mount = this.querySelector('[data-twc-reviews-mount]');
      var configNode = this.querySelector('[data-twc-reviews-config]');

      if (!this.mount || !configNode || !this.mount.id) {
        this.setState('unavailable');
        return;
      }

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        this.setState('unavailable');
        return;
      }

      if (!this.config || !this.config.store) {
        this.setState('unavailable');
        return;
      }

      this.setState('pending');

      // The theme editor has no scroll context worth waiting for, and a
      // merchant laying out the page needs the real feed in front of them.
      // The PDP summary above the fold shares this SKU-filtered widget's data.
      if (this.dataset.productSku || document.documentElement.classList.contains('shopify-design-mode') || !('IntersectionObserver' in global)) {
        this.start();
        return;
      }

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i += 1) {
            if (entries[i].isIntersecting) {
              self.stopObserving();
              self.start();
              return;
            }
          }
        },
        { rootMargin: NEAR_VIEWPORT }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.stopObserving();
      if (this.contentObserver) this.contentObserver.disconnect();
      if (this.giveUpTimer) global.clearTimeout(this.giveUpTimer);
      this.widget = null;
    }

    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    setState(state) {
      this.setAttribute('data-state', state);
    }

    start() {
      if (this.started) return;
      this.started = true;

      var self = this;
      loadWidgetScript()
        .then(function () {
          if (!self.isConnected || self.controller.signal.aborted) return;
          self.render();
        })
        .catch(function () {
          // A blocked CDN, an ad blocker or a dead account: the band collapses
          // exactly as it does when no store is configured. Silence is the
          // point — a third party must not spray a member's console.
          self.setState('unavailable');
        });
    }

    render() {
      var selector = '#' + (global.CSS && global.CSS.escape ? global.CSS.escape(this.mount.id) : this.mount.id);

      try {
        this.widget = new global.ReviewsWidget(selector, this.config);
      } catch (error) {
        this.setState('unavailable');
        return;
      }

      this.watchForContent();
    }

    /*
     * The constructor is synchronous but the reviews arrive over the network.
     * "Ready" means the mount actually holds rendered review rows; an account
     * with no reviews, or an API error, leaves it empty and the band collapses.
     */
    watchForContent() {
      var self = this;

      var settle = function () {
        if (!self.isConnected || self.controller.signal.aborted) return false;
        if (!self.mount.querySelector('.ElementsWidget')) return false;
        if (self.dataset.productSku && !self.syncProductSummary()) return false;
        self.setState('ready');
        if (self.contentObserver) {
          self.contentObserver.disconnect();
          self.contentObserver = null;
        }
        if (self.giveUpTimer) {
          global.clearTimeout(self.giveUpTimer);
          self.giveUpTimer = null;
        }
        return true;
      };

      if (settle()) return;

      this.contentObserver = new MutationObserver(settle);
      this.contentObserver.observe(this.mount, { childList: true, subtree: true });

      this.giveUpTimer = global.setTimeout(function () {
        if (self.contentObserver) {
          self.contentObserver.disconnect();
          self.contentObserver = null;
        }
        if (!self.isConnected || self.controller.signal.aborted) return;
        if (self.getAttribute('data-state') !== 'ready') self.setState('unavailable');
      }, READY_TIMEOUT_MS);
    }

    // Reuse the product widget's rendered summary: no second API request and
    // no change to the app snippet's independently configured store account.
    syncProductSummary() {
      var header = this.mount.querySelector('.header__group--left');
      var score = header && header.querySelector('.R-TextHeading--md');
      var stars = header && header.querySelector('.R-RatingStars__stars[role="img"]');
      var countNode = header && header.querySelector('.R-TextBody');
      var countMatch = countNode && countNode.textContent.match(/[0-9][0-9,]*/);
      var count = countMatch ? Number(countMatch[0].replace(/,/g, '')) : 0;
      if (!score || !stars || count < 1 || !this.mount.querySelector('.R-ContentList__item')) return false;

      var sku = this.dataset.productSku;
      document.querySelectorAll('[data-twc-product-review-summary]').forEach(function (summary) {
        if (summary.dataset.sku !== sku) return;
        summary.querySelector('[data-twc-product-review-score]').textContent = score.textContent.trim();
        summary.querySelector('[data-twc-product-review-stars]').replaceChildren(stars.cloneNode(true));
        summary.querySelector('[data-twc-product-review-count]').textContent = count.toLocaleString('en-AU') + ' Member reviews';
        summary.hidden = false;
      });
      return true;
    }
  };

  if (global.customElements) {
    global.customElements.define('twc-reviews-feed', TwcReviewsFeed);
  }
})(window);